import type { TimestampFormat } from "@cmmd-forge/contracts/settings";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisIcon,
  LoaderIcon,
  PanelRightCloseIcon,
} from "lucide-react";

import type { ActivePlanState, LatestProposedPlanState } from "../session-logic";
import { formatTimestamp } from "../timestampFormat";
import { cn } from "~/lib/utils";
import ChatMarkdown from "./ChatMarkdown";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "./ui/menu";
import { ScrollArea } from "./ui/scroll-area";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

interface PlanSidebarActionsMenuProps {
  readonly isCopied: boolean;
  readonly isSaving: boolean;
  readonly canSave: boolean;
  readonly onCopy: () => void;
  readonly onDownload: () => void;
  readonly onSave: () => void;
  readonly onCancelSave: () => void;
}

function PlanSidebarActionsMenu({
  isCopied,
  isSaving,
  canSave,
  onCopy,
  onDownload,
  onSave,
  onCancelSave,
}: PlanSidebarActionsMenuProps) {
  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            size="icon-xs"
            variant="ghost"
            className="text-muted-foreground/50 hover:text-foreground/70"
            aria-label="Plan actions"
          />
        }
      >
        <EllipsisIcon className="size-3.5" />
      </MenuTrigger>
      <MenuPopup align="end">
        <MenuItem onClick={onCopy}>{isCopied ? "Copied!" : "Copy to clipboard"}</MenuItem>
        <MenuItem onClick={onDownload}>Download as markdown</MenuItem>
        <MenuItem onClick={isSaving ? onCancelSave : onSave} disabled={!canSave}>
          {isSaving ? "Cancel save" : "Save to project"}
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

interface PlanSidebarHeaderProps extends PlanSidebarActionsMenuProps {
  readonly label: string;
  readonly activePlan: ActivePlanState | null;
  readonly hasPlanMarkdown: boolean;
  readonly timestampFormat: TimestampFormat;
  readonly onClose: () => void;
}

export function PlanSidebarHeader({
  label,
  activePlan,
  hasPlanMarkdown,
  timestampFormat,
  onClose,
  ...actions
}: PlanSidebarHeaderProps) {
  return (
    <div className="flex h-12 shrink-0 items-center justify-between border-b border-border/60 px-3">
      <div className="flex items-center gap-2">
        <Badge
          variant="secondary"
          className="rounded-md bg-info-surface px-1.5 py-0 text-[10px] font-semibold tracking-wide text-info-foreground uppercase"
        >
          {label}
        </Badge>
        {activePlan ? (
          <>
            <span className="select-none text-muted-foreground/30">·</span>
            <span className="text-[11px] text-muted-foreground/60">
              {formatTimestamp(activePlan.createdAt, timestampFormat)}
            </span>
          </>
        ) : null}
      </div>
      <div className="flex items-center gap-1">
        {hasPlanMarkdown ? <PlanSidebarActionsMenu {...actions} /> : null}
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={onClose}
                aria-label={`Close ${label.toLowerCase()} sidebar`}
                className="text-muted-foreground/50 hover:text-foreground/70"
              >
                <PanelRightCloseIcon className="size-3.5" />
              </Button>
            }
          />
          <TooltipPopup side="left">Collapse panel</TooltipPopup>
        </Tooltip>
      </div>
    </div>
  );
}

interface EmbeddedPlanActionsProps extends PlanSidebarActionsMenuProps {
  readonly activePlan: ActivePlanState | null;
  readonly timestampFormat: TimestampFormat;
}

export function EmbeddedPlanActions({
  activePlan,
  timestampFormat,
  ...actions
}: EmbeddedPlanActionsProps) {
  return (
    <div className="flex shrink-0 items-center justify-between border-b border-border/40 px-3 py-1.5">
      {activePlan ? (
        <span className="text-[11px] text-muted-foreground/60">
          {formatTimestamp(activePlan.createdAt, timestampFormat)}
        </span>
      ) : (
        <span />
      )}
      <PlanSidebarActionsMenu {...actions} />
    </div>
  );
}

function stepStatusIcon(status: string): React.ReactNode {
  if (status === "completed") {
    return (
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-success-surface text-success-foreground">
        <CheckIcon className="size-3" />
      </span>
    );
  }
  return status === "inProgress" ? (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-info-surface text-info-foreground">
      <LoaderIcon className="size-3 animate-spin" />
    </span>
  ) : (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/30">
      <span className="size-1.5 rounded-full bg-muted-foreground/30" />
    </span>
  );
}

interface PlanSidebarContentProps {
  readonly activePlan: ActivePlanState | null;
  readonly activeProposedPlan: LatestProposedPlanState | null;
  readonly displayedPlanMarkdown: string | null;
  readonly planTitle: string | null;
  readonly markdownCwd: string | undefined;
  readonly proposedPlanExpanded: boolean;
  readonly onToggleProposedPlan: () => void;
}

export function PlanSidebarContent({
  activePlan,
  activeProposedPlan,
  displayedPlanMarkdown,
  planTitle,
  markdownCwd,
  proposedPlanExpanded,
  onToggleProposedPlan,
}: PlanSidebarContentProps) {
  const planMarkdown = activeProposedPlan?.planMarkdown ?? null;
  return (
    <ScrollArea className="min-h-0 flex-1">
      <div className="p-3 space-y-4">
        {activePlan?.explanation ? (
          <p className="text-[13px] leading-relaxed text-muted-foreground/80">
            {activePlan.explanation}
          </p>
        ) : null}
        {activePlan && activePlan.steps.length > 0 ? (
          <div className="space-y-1">
            <p className="mb-2 text-[10px] font-semibold tracking-widest text-muted-foreground/40 uppercase">
              Steps
            </p>
            {activePlan.steps.map((step) => (
              <div
                key={`${step.status}:${step.step}`}
                className={cn(
                  "flex items-start gap-2.5 rounded-lg px-2.5 py-2 transition-colors duration-200",
                  step.status === "inProgress" ? "bg-info-surface" : undefined,
                  step.status === "completed" ? "bg-success-surface" : undefined,
                )}
              >
                <div className="mt-0.5">{stepStatusIcon(step.status)}</div>
                <p
                  className={cn(
                    "text-[13px] leading-snug",
                    step.status === "completed"
                      ? "text-muted-foreground/50 line-through decoration-muted-foreground/20"
                      : step.status === "inProgress"
                        ? "text-foreground/90"
                        : "text-muted-foreground/70",
                  )}
                >
                  {step.step}
                </p>
              </div>
            ))}
          </div>
        ) : null}
        {planMarkdown ? (
          <div className="space-y-2">
            <button
              type="button"
              className="group flex w-full items-center gap-1.5 text-left"
              onClick={onToggleProposedPlan}
            >
              {proposedPlanExpanded ? (
                <ChevronDownIcon className="size-3 shrink-0 text-muted-foreground/40 transition-transform" />
              ) : (
                <ChevronRightIcon className="size-3 shrink-0 text-muted-foreground/40 transition-transform" />
              )}
              <span className="text-[10px] font-semibold tracking-widest text-muted-foreground/40 uppercase group-hover:text-muted-foreground/60">
                {planTitle ?? "Full Plan"}
              </span>
            </button>
            {proposedPlanExpanded ? (
              <div className="rounded-lg border border-border/50 bg-background/50 p-3">
                <ChatMarkdown
                  text={displayedPlanMarkdown ?? ""}
                  cwd={markdownCwd}
                  isStreaming={false}
                />
              </div>
            ) : null}
          </div>
        ) : null}
        {!activePlan && !planMarkdown ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-[13px] text-muted-foreground/40">No active plan yet.</p>
            <p className="mt-1 text-[11px] text-muted-foreground/30">
              Plans will appear here when generated.
            </p>
          </div>
        ) : null}
      </div>
    </ScrollArea>
  );
}
